import { useState, useEffect, useRef } from 'react';
import { supabase, uploadProductImage } from '../supabase';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

// ─── Styles ───────────────────────────────────────────────
const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Josefin Sans', sans-serif" },
  sidebar: {
    width: '220px', background: '#0d0d0d', color: '#ccc',
    padding: '0', flexShrink: 0, display: 'flex', flexDirection: 'column',
  },
  sidebarLogo: {
    padding: '28px 24px 24px',
    fontFamily: "'Cormorant Garamond', serif", fontSize: '18px',
    letterSpacing: '6px', color: '#fff', textTransform: 'uppercase',
    borderBottom: '1px solid #222',
  },
  sidebarItem: (active) => ({
    display: 'block', padding: '14px 24px', cursor: 'pointer',
    fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase',
    color: active ? '#fff' : '#777', background: active ? '#1a1a1a' : 'transparent',
    border: 'none', width: '100%', textAlign: 'left',
    borderLeft: active ? '2px solid #fff' : '2px solid transparent',
    transition: 'all 0.2s',
  }),
  main: { flex: 1, background: '#f8f8f8', padding: '40px 48px', overflow: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '36px' },
  h1: { fontSize: '12px', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300, color: '#111' },
  card: { background: '#fff', border: '1px solid #eee', padding: '32px', marginBottom: '24px' },
  statCard: { background: '#fff', border: '1px solid #eee', padding: '28px', textAlign: 'center' },
  statNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: '36px', color: '#111', display: 'block' },
  statLabel: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginTop: '4px', display: 'block' },
  btn: (variant = 'primary') => ({
    padding: '10px 24px', border: 'none', cursor: 'pointer',
    fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px',
    letterSpacing: '2px', textTransform: 'uppercase',
    background: variant === 'primary' ? '#111' : variant === 'danger' ? '#c0392b' : '#fff',
    color: variant === 'ghost' ? '#111' : '#fff',
    border: variant === 'ghost' ? '1px solid #ddd' : 'none',
    transition: 'opacity 0.2s',
  }),
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px',
    outline: 'none', background: '#fff', marginBottom: '16px',
  },
  label: { display: 'block', fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginBottom: '6px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: { textAlign: 'left', fontWeight: 300, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '10px', color: '#888', padding: '12px 16px', borderBottom: '1px solid #eee' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' },
};

// ─── Login Screen ──────────────────────────────────────────
function Login({ onLogin }) {
  const [email, setEmail] = useState(ADMIN_EMAIL || '');
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email || !pw) return setError('Email et mot de passe requis');
    setLoading(true);
    setError('');
    const { error: err } = await supabase.auth.signInWithPassword({ email, password: pw });
    setLoading(false);
    if (err) return setError('Identifiants incorrects');
    onLogin();
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f8f8' }}>
      <div style={{ background: '#fff', padding: '60px', border: '1px solid #eee', width: '360px', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', letterSpacing: '4px', marginBottom: '8px', textTransform: 'uppercase' }}>Norelio</div>
        <div style={{ fontSize: '10px', letterSpacing: '3px', color: '#888', textTransform: 'uppercase', marginBottom: '40px' }}>Admin</div>
        {!ADMIN_EMAIL && (
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
            style={{ ...s.input, textAlign: 'center', letterSpacing: '1px' }} />
        )}
        <input type="password" placeholder="Mot de passe" value={pw} onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          style={{ ...s.input, textAlign: 'center', letterSpacing: '4px' }} />
        {error && <div style={{ color: '#c0392b', fontSize: '11px', marginBottom: '12px', letterSpacing: '1px' }}>{error}</div>}
        <button style={s.btn()} onClick={submit} disabled={loading}>
          {loading ? 'Connexion...' : 'Connexion'}
        </button>
      </div>
    </div>
  );
}

// ─── Product Form Modal ────────────────────────────────────
function ProductModal({ product, onSave, onClose }) {
  const [form, setForm] = useState(product || { name: '', price: '', category: 'Bracelets', description: '', images: [], in_stock: true, stock_quantity: 0, sizes: [], size_stock: {} });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [sizeInput, setSizeInput] = useState('');
  const dragIdx = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setUploadError('');
    try {
      const urls = await Promise.all(files.map(uploadProductImage));
      set('images', [...(form.images || []), ...urls]);
    } catch (err) {
      setUploadError(`Erreur upload : ${err.message}. Vérifiez que le bucket "product-images" existe et est public dans Supabase Storage.`);
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeImage = (i) => set('images', form.images.filter((_, j) => j !== i));

  const handleDragStart = (i) => { dragIdx.current = i; };
  const handleDrop = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const imgs = [...form.images];
    const [moved] = imgs.splice(from, 1);
    imgs.splice(i, 0, moved);
    set('images', imgs);
    dragIdx.current = null;
  };

  const handleSave = async () => {
    if (!form.name || !form.price) return alert('Nom et prix requis.');
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), images: form.images.filter(Boolean) };
    let result, err;
    if (form.id) {
      const res = await supabase.from('products').update(payload).eq('id', form.id).select().single();
      result = res.data; err = res.error;
    } else {
      const res = await supabase.from('products').insert(payload).select().single();
      result = res.data; err = res.error;
    }
    setSaving(false);
    if (err) return alert(`Erreur : ${err.message}\n\nVérifiez que vous êtes bien connecté et que les policies RLS autorisent l'écriture.`);
    onSave(result);
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const modal = { background: '#fff', width: '560px', maxHeight: '90vh', overflow: 'auto', padding: '40px' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <h2 style={{ fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300 }}>{form.id ? 'Modifier' : 'Nouveau'} Produit</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#888' }}>×</button>
        </div>

        <label style={s.label}>Nom *</label>
        <input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="L'homme idéal" />

        <label style={s.label}>Prix (MAD) *</label>
        <input style={s.input} type="number" value={form.price} onChange={e => set('price', e.target.value)} placeholder="229" />

        <label style={s.label}>Catégorie</label>
        <select value={form.category} onChange={e => set('category', e.target.value)}
          style={{ ...s.input, cursor: 'pointer' }}>
          <option>Bracelets</option>
          <option>Bagues</option>
          <option>Colliers</option>
          <option>Autre</option>
        </select>

        {/* Sizes + per-size stock — only for Bagues */}
        {form.category === 'Bagues' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={s.label}>Tailles & stock</label>

            {(form.sizes || []).length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                {(form.sizes || []).map(sz => (
                  <div key={sz} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '11px', letterSpacing: '2px', width: '40px' }}>T.{sz}</span>
                    <input
                      type="number" min="0"
                      value={(form.size_stock || {})[sz] ?? 0}
                      onChange={e => set('size_stock', { ...(form.size_stock || {}), [sz]: parseInt(e.target.value) || 0 })}
                      style={{ ...s.input, marginBottom: 0, width: '80px' }}
                    />
                    <span style={{ fontSize: '10px', color: '#aaa', letterSpacing: '1px' }}>en stock</span>
                    <button onClick={() => {
                      const newSizes = form.sizes.filter(s => s !== sz);
                      const newStock = { ...(form.size_stock || {}) };
                      delete newStock[sz];
                      set('sizes', newSizes);
                      set('size_stock', newStock);
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '16px', marginLeft: 'auto' }}>×</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                style={{ ...s.input, marginBottom: 0, width: '80px' }}
                value={sizeInput}
                onChange={e => setSizeInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && sizeInput.trim()) {
                    const sz = sizeInput.trim();
                    if (!form.sizes?.includes(sz)) {
                      set('sizes', [...(form.sizes || []), sz]);
                      set('size_stock', { ...(form.size_stock || {}), [sz]: 0 });
                    }
                    setSizeInput('');
                  }
                }}
                placeholder="ex: 9"
              />
              <button onClick={() => {
                const sz = sizeInput.trim();
                if (sz && !form.sizes?.includes(sz)) {
                  set('sizes', [...(form.sizes || []), sz]);
                  set('size_stock', { ...(form.size_stock || {}), [sz]: 0 });
                }
                setSizeInput('');
              }} style={{ ...s.btn('ghost'), padding: '0 16px' }}>+ Taille</button>
            </div>
            <p style={{ fontSize: '10px', color: '#aaa', letterSpacing: '1px', marginTop: '6px' }}>Ajoutez chaque taille puis définissez son stock</p>
          </div>
        )}

        <label style={s.label}>Description</label>
        <textarea rows={4} style={{ ...s.input, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Description du produit..." />

        <label style={s.label}>Photos du produit</label>

        {/* Thumbnails of uploaded images — draggable to reorder */}
        {(form.images || []).length > 0 && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {form.images.map((url, i) => (
              <div
                key={url}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(i)}
                style={{ position: 'relative', width: '90px', height: '90px', cursor: 'grab' }}
              >
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', border: i === 0 ? '2px solid #111' : '1px solid #eee' }} />
                {i === 0 && (
                  <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '8px', letterSpacing: '1px', textAlign: 'center', padding: '3px 0', textTransform: 'uppercase' }}>Principale</span>
                )}
                <button onClick={() => removeImage(i)} style={{ position: 'absolute', top: '3px', right: '3px', width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', lineHeight: '20px', textAlign: 'center', padding: 0 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* File picker */}
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          padding: '10px 20px', border: '1px dashed #bbb', cursor: uploading ? 'wait' : 'pointer',
          fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px',
          textTransform: 'uppercase', color: '#555', marginBottom: '8px',
          background: uploading ? '#f8f8f8' : '#fff',
        }}>
          {uploading ? 'Téléchargement...' : '+ Ajouter des photos'}
          <input type="file" accept="image/*" multiple onChange={handleFileChange} disabled={uploading} style={{ display: 'none' }} />
        </label>
        {uploadError && <p style={{ color: '#c0392b', fontSize: '11px', marginBottom: '12px', lineHeight: '1.5' }}>{uploadError}</p>}

        {form.category !== 'Bagues' && (
          <>
            <label style={s.label}>Quantité en stock</label>
            <input
              style={{ ...s.input, marginBottom: '24px' }}
              type="number" min="0"
              value={form.stock_quantity}
              onChange={e => {
                const q = parseInt(e.target.value) || 0;
                set('stock_quantity', q);
                set('in_stock', q > 0);
              }}
            />
          </>
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={s.btn()} onClick={handleSave} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
          <button style={s.btn('ghost')} onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

// ─── Products Tab ──────────────────────────────────────────
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('products').select('*').order('created_at', { ascending: false });
    setProducts(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await supabase.from('products').delete().eq('id', id);
    setProducts(p => p.filter(x => x.id !== id));
  };

  const handleSave = (saved) => {
    setProducts(prev => {
      const exists = prev.find(p => p.id === saved.id);
      return exists ? prev.map(p => p.id === saved.id ? saved : p) : [saved, ...prev];
    });
    setShowModal(false);
    setEditing(null);
  };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.h1}>Produits ({products.length})</h1>
        <button style={s.btn()} onClick={() => { setEditing(null); setShowModal(true); }}>+ Nouveau produit</button>
      </div>

      <div style={s.card}>
        {loading ? <div style={{ textAlign: 'center', padding: '40px', color: '#888', fontSize: '12px', letterSpacing: '2px' }}>Chargement...</div> : (
          <table style={s.table}>
            <thead>
              <tr>
                {['Image', 'Nom', 'Catégorie', 'Prix', 'Stock', 'Actions'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id}>
                  <td style={s.td}>
                    <img src={p.images?.[0] || 'https://via.placeholder.com/60'} alt={p.name} style={{ width: '52px', height: '52px', objectFit: 'cover', display: 'block' }} />
                  </td>
                  <td style={{ ...s.td, fontFamily: "'Cormorant Garamond', serif", fontSize: '16px' }}>{p.name}</td>
                  <td style={{ ...s.td, fontSize: '11px', letterSpacing: '1px', color: '#666' }}>{p.category}</td>
                  <td style={{ ...s.td, fontWeight: 400 }}>Dh {Number(p.price).toFixed(2)}</td>
                  <td style={s.td}>
                    <span style={{ display: 'inline-block', padding: '3px 10px', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', background: p.in_stock ? '#e8f5e9' : '#fce4e4', color: p.in_stock ? '#2e7d32' : '#c62828' }}>
                      {p.in_stock ? 'En stock' : 'Épuisé'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={s.btn('ghost')} onClick={() => { setEditing(p); setShowModal(true); }}>Modifier</button>
                      <button style={s.btn('danger')} onClick={() => handleDelete(p.id)}>Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && <ProductModal product={editing} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} />}
    </div>
  );
}

// ─── Orders Tab ────────────────────────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
      setOrders(data || []);
      setLoading(false);
    };
    load();
  }, []);

  const updateStatus = async (id, status) => {
    await supabase.from('orders').update({ status }).eq('id', id);
    setOrders(o => o.map(x => x.id === id ? { ...x, status } : x));
  };

  const statusColor = { pending: '#f0ad4e', confirmed: '#5bc0de', shipped: '#5cb85c', delivered: '#337ab7', cancelled: '#d9534f' };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.h1}>Commandes ({orders.length})</h1>
      </div>
      <div style={s.card}>
        {loading ? <div style={{ textAlign: 'center', padding: '40px', color: '#888', fontSize: '12px', letterSpacing: '2px' }}>Chargement...</div>
          : orders.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#888', fontFamily: "'Cormorant Garamond', serif", fontSize: '18px' }}>Aucune commande pour l'instant.</div>
          : (
          <table style={s.table}>
            <thead>
              <tr>{['Client', 'Téléphone', 'Produit', 'Qté', 'Total', 'Statut', 'Date'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td style={{ ...s.td, fontFamily: "'Cormorant Garamond', serif", fontSize: '15px' }}>{o.customer_name}</td>
                  <td style={{ ...s.td, fontSize: '12px' }}>{o.customer_phone}</td>
                  <td style={{ ...s.td, fontSize: '11px', color: '#666' }}>{o.product_id?.slice(0, 8)}…</td>
                  <td style={s.td}>{o.quantity}</td>
                  <td style={s.td}>Dh {Number(o.total).toFixed(2)}</td>
                  <td style={s.td}>
                    <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)} style={{ padding: '4px 8px', border: `1px solid ${statusColor[o.status] || '#ddd'}`, background: '#fff', fontSize: '11px', cursor: 'pointer', color: statusColor[o.status] || '#333', fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '1px' }}>
                      {['pending','confirmed','shipped','delivered','cancelled'].map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </td>
                  <td style={{ ...s.td, fontSize: '11px', color: '#888' }}>{new Date(o.created_at).toLocaleDateString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard Overview ────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState({ products: 0, orders: 0, revenue: 0, pending: 0 });
  useEffect(() => {
    const load = async () => {
      const { count: pc } = await supabase.from('products').select('*', { count: 'exact', head: true });
      const { data: orders } = await supabase.from('orders').select('total, status');
      const revenue = (orders || []).reduce((s, o) => s + Number(o.total || 0), 0);
      const pending = (orders || []).filter(o => o.status === 'pending').length;
      setStats({ products: pc || 0, orders: (orders || []).length, revenue, pending });
    };
    load();
  }, []);

  return (
    <div>
      <h1 style={{ ...s.h1, marginBottom: '36px' }}>Tableau de bord</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '40px' }}>
        {[
          ['Produits', stats.products, ''],
          ['Commandes', stats.orders, ''],
          ['Revenu total', `Dh ${stats.revenue.toFixed(0)}`, ''],
          ['En attente', stats.pending, ''],
        ].map(([label, value]) => (
          <div key={label} style={s.statCard}>
            <span style={s.statNum}>{value}</span>
            <span style={s.statLabel}>{label}</span>
          </div>
        ))}
      </div>
      <div style={s.card}>
        <h2 style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: '#888', marginBottom: '16px', fontWeight: 300 }}>Guide de déploiement</h2>
        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', lineHeight: '2', color: '#444' }}>
          <p>1. <strong>Supabase</strong> — créer un projet gratuit sur supabase.com, copier URL + clé dans <code>.env</code></p>
          <p>2. <strong>Build</strong> — <code>npm run build</code></p>
          <p>3. <strong>Déployer</strong> — glisser le dossier <code>build/</code> sur Netlify.com (gratuit, domaine personnalisé inclus)</p>
          <p>4. <strong>Domaine</strong> — dans Netlify → Domain settings → ajouter norelio.com</p>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Tab ──────────────────────────────────────────
function SettingsTab() {
  const [form, setForm] = useState({ siteName: 'Norelio', currency: 'MAD', adminEmail: 'contact@norelio.com', supabaseUrl: '', supabaseKey: '' });
  const [saved, setSaved] = useState(false);
  const save = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  return (
    <div>
      <h1 style={{ ...s.h1, marginBottom: '36px' }}>Paramètres</h1>
      <div style={s.card}>
        <h2 style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: '#888', marginBottom: '28px', fontWeight: 300 }}>Configuration Supabase</h2>
        <label style={s.label}>URL du projet Supabase</label>
        <input style={s.input} value={form.supabaseUrl} onChange={e => setForm(f => ({ ...f, supabaseUrl: e.target.value }))} placeholder="https://xxxx.supabase.co" />
        <label style={s.label}>Clé anonyme (anon key)</label>
        <input style={s.input} type="password" value={form.supabaseKey} onChange={e => setForm(f => ({ ...f, supabaseKey: e.target.value }))} placeholder="eyJhbGciOi..." />
        <p style={{ fontSize: '11px', color: '#888', letterSpacing: '1px', marginBottom: '24px' }}>
          Mettez ces valeurs dans votre fichier <code>.env</code> : <code>REACT_APP_SUPABASE_URL</code> et <code>REACT_APP_SUPABASE_ANON_KEY</code>
        </p>

        <h2 style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: '#888', marginBottom: '20px', marginTop: '8px', fontWeight: 300 }}>Boutique</h2>
        <label style={s.label}>Nom de la boutique</label>
        <input style={s.input} value={form.siteName} onChange={e => setForm(f => ({ ...f, siteName: e.target.value }))} />
        <label style={s.label}>Devise</label>
        <input style={s.input} value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} />
        <label style={s.label}>Email de contact</label>
        <input style={s.input} value={form.adminEmail} onChange={e => setForm(f => ({ ...f, adminEmail: e.target.value }))} />

        <button style={s.btn()} onClick={save}>{saved ? 'Enregistré ✓' : 'Enregistrer'}</button>
      </div>
    </div>
  );
}

// ─── Main Admin ────────────────────────────────────────────
export default function Admin() {
  const [loggedIn, setLoggedIn] = useState(null); // null = checking session
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setLoggedIn(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => setLoggedIn(!!session));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (loggedIn === null) return null;
  if (!loggedIn) return <Login onLogin={() => setLoggedIn(true)} />;

  const tabs = [
    ['dashboard', 'Tableau de bord'],
    ['products', 'Produits'],
    ['orders', 'Commandes'],
    ['settings', 'Paramètres'],
  ];

  return (
    <div style={s.page}>
      <aside style={s.sidebar}>
        <div style={s.sidebarLogo}>Norelio</div>
        {tabs.map(([key, label]) => (
          <button key={key} style={s.sidebarItem(tab === key)} onClick={() => setTab(key)}>{label}</button>
        ))}
        <div style={{ marginTop: 'auto', borderTop: '1px solid #222' }}>
          <button style={s.sidebarItem(false)} onClick={() => window.open('/', '_blank')}>↗ Voir la boutique</button>
          <button style={s.sidebarItem(false)} onClick={() => supabase.auth.signOut()}>Déconnexion</button>
        </div>
      </aside>
      <div style={s.main}>
        {tab === 'dashboard' && <Dashboard />}
        {tab === 'products' && <ProductsTab />}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}
